"use client";

import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

/**
 * Migrated from `@radix-ui/react-toggle` to Base UI, together with
 * `toggle-group.tsx`.
 *
 * The two are one component family, not two neighbours: `ToggleGroupItem`
 * builds its class list from `toggleVariants` below, so the `data-[state=on]` →
 * `data-[pressed]` rename in this file is load-bearing for both. Migrating
 * either half alone would leave the shared string half-renamed.
 *
 * `Toggle`'s own API needed almost nothing: Base UI uses the same `pressed` and
 * `onPressedChange` names, and its button is natively disabled
 * (`focusableWhenDisabled: false`), so the `disabled:` Tailwind variants below
 * keep matching — unlike Accordion's trigger (#6176), which needed
 * `data-[disabled]:` added alongside them.
 *
 * What did change is the pressed-state attribute, and that one reaches callers:
 * `ReplenishmentPage` overrides the pressed fill through it and is updated in
 * the same commit.
 */
const toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium hover:bg-muted hover:text-muted-foreground disabled:pointer-events-none disabled:opacity-50 data-[pressed]:bg-accent data-[pressed]:text-accent-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none transition-[color,box-shadow] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline:
          "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-9 px-2 min-w-9",
        sm: "h-8 px-1.5 min-w-8",
        lg: "h-10 px-2.5 min-w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ToggleProps = Omit<
  TogglePrimitive.Props<string>,
  "onPressedChange" | "className"
> &
  VariantProps<typeof toggleVariants> & {
    /**
     * Base UI passes `eventDetails` as a second argument. Forwarding it would
     * widen the callback every caller is typed against, for a value none of
     * them want.
     */
    onPressedChange?: (pressed: boolean) => void;
    className?: string;
  };

function Toggle({
  className,
  variant,
  size,
  onPressedChange,
  ...props
}: ToggleProps) {
  return (
    <TogglePrimitive
      data-slot="toggle"
      onPressedChange={(pressed) => onPressedChange?.(pressed)}
      className={cn(toggleVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Toggle, toggleVariants };
